'use client'

import * as React from 'react'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import * as Tooltip from '@radix-ui/react-tooltip'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { decodeHtmlEntities } from 'utils/decode-html-entities'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { CheckIcon } from 'primitives/icons'

import { Button } from '@web/components/prod/button/button'
import { Card } from './card'
import { useUserProfileProgress } from '../_hooks/use-user-profile-progress'
import { ContentInterestsIcon, PaletteFillIcon } from './icons'
import { Typography } from '@repo/design-system-cf/components/typography'

export function MyAccountProfileWithProgress() {
  return (
    <Card>
      <div className="@xl:flex-row @xl:items-stretch @xl:gap-12 flex flex-col items-center gap-4 px-2">
        <UserAvatarWithProgressAndLabel />
        <UserProfileInformation />
      </div>
    </Card>
  )
}

function UserProfileInformation() {
  const locale = useLocale()
  const t = useTranslations('my_account.profile_with_progress')
  const { isLoading, firstName, lastName, craftingTypes, contentInterests } =
    useUserProfileProgress()
  const { data } = useUser()

  const fullName =
    firstName || lastName
      ? `${decodeHtmlEntities(firstName || '')} ${decodeHtmlEntities(lastName || '')}`
      : t('default_user')
  const craftsLabel = craftingTypes.length ? craftingTypes.join(' • ') : t('crafts_empty')
  const contentInterestsLabel = contentInterests.length
    ? contentInterests.join(' • ')
    : t('content_interests_empty')
  return (
    <div className="@xl:w-auto flex w-full grow flex-col gap-4 truncate">
      <div className="@xl:flex-row flex flex-col items-center justify-between gap-3">
        <div className="@xl:self-auto @xl:text-left self-stretch truncate text-center">
          <p className="text-20 inline font-bold">{isLoading ? '\u00A0' : fullName}</p>
        </div>
        <div className="md:hidden">
          <Typography variant="body-compact-01">{`${t('info_account')}: #${data?.id}`}</Typography>
        </div>
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={addOrReplaceLocaleInUrl(AppRoutes.MY_ACCOUNT_PROFILE, locale)}>
            {t('edit_profile')}
          </Link>
        </Button>
      </div>
      <div className="@xl:self-stretch flex flex-col gap-4 self-center">
        <div className="flex w-full items-center gap-2.5">
          <PaletteFillIcon className="h-4 w-4 shrink-0" />
          <span className="@xl:whitespace-nowrap @xl:truncate @xl:pr-4 whitespace-normal">
            {craftsLabel}
          </span>
        </div>
        <div className="flex w-full items-center gap-2.5">
          <ContentInterestsIcon className="h-4 w-4 shrink-0" />
          <span className="@xl:whitespace-nowrap @xl:truncate @xl:pr-4 whitespace-normal">
            {contentInterestsLabel}
          </span>
        </div>
      </div>
    </div>
  )
}

function UserAvatarWithProgressAndLabel() {
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false)
  const { data: user } = useUser()
  const { progress } = useUserProfileProgress()
  const t = useTranslations('my_account.profile_with_progress')
  return (
    <Tooltip.Provider>
      <Tooltip.Root open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
        <Tooltip.Trigger
          type="button"
          onClick={() => setIsTooltipOpen(true)}
          className="flex cursor-pointer flex-col gap-4"
        >
          <UserAvatarWithProgress progress={progress} avatarUrl={user?.avatarUrl || ''} />
          <p className="text-12 text-center font-semibold">
            {progress}% {t('progress_completed')}
          </p>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <>
            <Tooltip.Content
              align="start"
              side="bottom"
              sideOffset={10}
              className="bg-cf-black rounded-2 text-cf-white hidden w-80 p-4 text-sm lg:flex"
            >
              <ProgressCheckmarks />
              <Tooltip.Arrow className="fill-cf-black" />
            </Tooltip.Content>
            <Tooltip.Content
              align="center"
              side="bottom"
              sideOffset={10}
              className="bg-cf-black rounded-2 text-cf-white flex w-80 p-4 text-sm lg:hidden"
            >
              <ProgressCheckmarks />
              <Tooltip.Arrow className="fill-cf-black" />
            </Tooltip.Content>
          </>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
}

function ProgressCheckmarks() {
  const t = useTranslations('my_account.profile_with_progress.progress_tooltip')
  const { firstName, lastName, isAvatarUserUploaded, contentInterests, craftingTypes } =
    useUserProfileProgress()
  const checks = [
    {
      label: t('first_name'),
      checked: Boolean(firstName),
    },
    {
      label: t('last_name'),
      checked: Boolean(lastName),
    },
    {
      label: t('avatar_url'),
      checked: isAvatarUserUploaded,
    },
    {
      label: t('content_interests'),
      checked: Boolean(contentInterests.length),
    },
    {
      label: t('crafting_types'),
      checked: Boolean(craftingTypes.length),
    },
  ]
  return (
    <div className="flex flex-col gap-2">
      <p>{t('title')}</p>
      <div>
        {checks.map(({ label, checked }, index) => (
          <div className="flex items-center gap-2" key={index}>
            <div className="flex h-2 w-2 shrink-0 items-center justify-center">
              {checked ? (
                <CheckIcon className="h-2 w-2 [&_path]:stroke-[6]" />
              ) : (
                <div className="bg-cf-white h-1 w-1 rounded-full" />
              )}
            </div>
            <p>{label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function UserAvatarWithProgress({
  progress = 100,
  avatarUrl,
}: {
  progress?: number
  avatarUrl?: string
}) {
  return (
    <div className="relative flex h-28 w-28 items-center justify-center self-center">
      <div className="absolute inset-0">
        <svg
          className="h-full w-full"
          width="200"
          height="200"
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* guide */}
          <circle
            cx="100"
            cy="100"
            r="90"
            strokeWidth="5"
            className="stroke-cf-sf-gray-50 fill-none"
          />
          {/* progress */}
          <circle
            className="stroke-cf-sf-green-300  fill-none [stroke-dashoffset:var(--progress)]"
            cx="100"
            cy="100"
            r="90"
            strokeWidth="8"
            strokeDasharray="565.48"
            transform="rotate(-90 100 100)"
            style={{ '--progress': `calc(565.48 - (565.48 * ${progress} / 100))` }}
          />
        </svg>
      </div>
      {avatarUrl ? (
        <Image
          src={avatarUrl}
          alt="Your avatar"
          className="relative h-20 w-20 rounded-full"
          width={72}
          height={72}
        />
      ) : (
        <div className="bg-cf-sf-gray-50 relative h-20 w-20 rounded-full" />
      )}
    </div>
  )
}
